import React, { useState } from "react";
import { PageShell, EditableSection } from "./PageShell";
import { Link } from "react-router-dom";
import { Search, Loader2, Wallet, CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const HELP = [{
  heading: "About your code",
  body: "Your Scentodore gift card code was included in the email you received after purchase. It's usually 10\u201316 characters long — a mix of letters and numbers. Enter it exactly as shown; we ignore spaces and dashes so you can paste it freely.",
}];

const PRIVACY = [{
  heading: "How we protect your balance",
  body: "For security, Shopify does not expose full gift-card codes to anyone — including us. We look up your card using the last four characters of what you enter. If two of your cards happen to end in the same four characters, we ask you to contact concierge so we can verify by hand.",
}];

function formatCurrency(amount, currency = "CAD") {
  return new Intl.NumberFormat("en-CA", { style: "currency", currency, minimumFractionDigits: 2 }).format(amount);
}

export default function GiftCardBalancePage() {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { balance, currency, initial_value, expires_on, enabled, masked_code }
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const { data } = await api.post("/gift-cards/balance", { code });
      setResult(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message = typeof detail === "string"
        ? detail
        : detail?.message
        || (err?.response?.status === 503
          ? "Balance lookup isn't quite ready yet — please contact concierge and we'll check your balance by hand."
          : "We couldn't find a gift card matching that code. Please double-check and try again.");
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell
      slug="gift-card-balance"
      testid="gift-card-balance"
      eyebrow="Gift Card Balance"
      title="Check your remaining balance."
      intro="Enter your Scentodore gift-card code below to see the amount available on it. Cards never expire and can be used across the entire collection."
    >
      <form onSubmit={onSubmit} data-testid="gift-card-balance-form" className="mb-14 max-w-2xl">
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059]">Gift card code</span>
          <div className="mt-2 grid grid-cols-[1fr_auto] gap-3">
            <input
              required
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="e.g. ABCD-1234-EFGH-5678"
              autoComplete="off"
              spellCheck={false}
              data-testid="gift-card-balance-input"
              className="px-4 py-3.5 rounded-xl border border-black/10 bg-[#FAF8F5] text-[15px] tracking-[0.08em] font-mono focus:outline-none focus:border-[#C5A059]"
            />
            <button
              type="submit"
              disabled={loading || !code.trim()}
              data-testid="gift-card-balance-submit"
              className="inline-flex items-center justify-center gap-2 bg-[#1A1918] text-[#FAF8F5] px-5 py-3.5 rounded-xl text-xs uppercase tracking-[0.18em] hover:bg-[#C5A059] hover:text-[#0B0A08] transition-colors disabled:opacity-60"
            >
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Checking</> : <><Search className="h-4 w-4" /> Check balance</>}
            </button>
          </div>
        </label>
      </form>

      {result ? (
        <div className="mb-14 max-w-2xl p-6 sm:p-8 rounded-2xl border border-[#C5A059]/40 bg-[#F1ECE4]/60" data-testid="gift-card-balance-result">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 rounded-full bg-[#C5A059]/15 flex items-center justify-center text-[#C5A059] shrink-0">
              <Wallet className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mb-1.5">Remaining balance</div>
              <div className="font-serif text-4xl sm:text-5xl text-[#1A1918]">
                {formatCurrency(result.balance, result.currency)}
              </div>
              {result.masked_code ? (
                <div className="text-[13px] text-[#1A1918]/60 mt-1 font-mono tracking-[0.05em]">Card ending: {result.masked_code}</div>
              ) : null}
              <div className="mt-4 grid sm:grid-cols-2 gap-2 text-[13.5px] text-[#1A1918]/75">
                {result.initial_value ? (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[#1A1918]/50">Original value:</span>
                    <span>{formatCurrency(result.initial_value, result.currency)}</span>
                  </div>
                ) : null}
                {result.expires_on ? (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[#1A1918]/50">Expires:</span>
                    <span>{new Date(result.expires_on).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5 text-[#C5A059]" />
                    <span>Never expires</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <span className="text-[#1A1918]/50">Status:</span>
                  <span className={result.enabled ? "text-emerald-700" : "text-red-700"}>{result.enabled ? "Active" : "Disabled"}</span>
                </div>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/shop"
                  data-testid="gift-card-balance-shop-cta"
                  className="inline-flex items-center gap-2 bg-[#1A1918] text-[#FAF8F5] px-5 py-3 rounded-xl text-xs uppercase tracking-[0.18em] hover:bg-[#C5A059] hover:text-[#0B0A08] transition-colors"
                >
                  Use it — shop the collection <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="mb-14 max-w-2xl p-5 rounded-2xl border border-red-200 bg-red-50/60" data-testid="gift-card-balance-error">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-700/80 mt-0.5 shrink-0" />
            <div className="text-sm text-[#1A1918]/80 leading-[1.7]">
              {error}
              <div className="mt-2">
                <Link to="/contact" className="text-[#C5A059] hover:underline">Contact concierge →</Link>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <EditableSection slug="gift-card-balance" sectionKey="help" fallback={HELP} />
      <EditableSection slug="gift-card-balance" sectionKey="privacy" fallback={PRIVACY} />

      <div className="-mt-8">
        <Link
          to="/gift-cards"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] text-[#1A1918]/60 hover:text-[#C5A059]"
        >
          <ArrowRight className="h-3.5 w-3.5 -scale-x-100" /> Back to buying gift cards
        </Link>
      </div>
    </PageShell>
  );
}
